import { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Point from '@arcgis/core/geometry/Point';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { IncidentListItemData } from './Incidents';
import { IncidentPopupContent } from './IncidentPopupContent';

interface IncidentMapButtonProps {
  incident: IncidentListItemData;
}

export const IncidentMapButton: React.FC<IncidentMapButtonProps> = ({
  incident,
}) => {
  const { mapView } = useUscgMapContext();

  const openIncidentPopup = useCallback(async () => {
    if (!mapView || !mapView.popup || !incident.latLong) return;

    const popupContent = document.createElement('div');
    const root = createRoot(popupContent);
    root.render(<IncidentPopupContent incident={incident} />);

    const popupLocation = new Point({
      longitude: incident.latLong.longitude,
      latitude: incident.latLong.latitude,
    });

    await mapView.openPopup({
      title: incident.name,
      location: popupLocation,
      content: popupContent,
    });

    const popup = mapView.popup;
    if (!popup) return;

    const handle = popup.watch('visible', (visible) => {
      if (!visible) {
        root.unmount();
        handle.remove();
      }
    });
  }, [incident, mapView]);

  return (
    <StylishNewButton
      className="button--icon"
      title="Jump to map"
      aria-label={`Jump to ${incident.name} on map`}
      onClick={async (e) => {
        e.stopPropagation();

        if (!mapView || !incident.latLong) return;

        const zoomLocation = new Point({
          longitude: incident.latLong.longitude,
          latitude: incident.latLong.latitude,
        });

        await mapView.goTo(zoomLocation);
        await openIncidentPopup();
      }}
    >
      <SharedIcon iconName="map" size="sm" />
    </StylishNewButton>
  );
};
